// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Dialog for displaying the latest CRS bill summary (from Congress.gov data).
 */
import type { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AlertCircle, Tag } from 'lucide-react'
import { Badge } from '@/components/ui/badge'

import type { BillSummaryEntry } from '../../services/federal-bills.service'
import { formatDate } from '../../utils/formatters'
import { sanitizeHtml } from '@/applications/shared'

interface BillSummaryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  summaries: BillSummaryEntry[]
  billNumber: string
  testId?: string
}

export const BillSummaryDialog: FC<BillSummaryDialogProps> = ({
  open,
  onOpenChange,
  summaries,
  billNumber,
  testId,
}) => {
  const summary = summaries[0]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-3xl max-h-[80vh] overflow-y-auto"
        data-testid={testId}
      >
        <DialogHeader>
          <DialogTitle>Summary - {billNumber}</DialogTitle>
          <DialogDescription>
            {summary
              ? `${summary.actionDesc ?? 'CRS Summary'}${summary.actionDate ? ` — ${formatDate(summary.actionDate)}` : ''}`
              : 'Congressional Research Service summary'}
          </DialogDescription>
        </DialogHeader>

        {summary ? (
          <>
            {summary.policyArea && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Tag className="h-3.5 w-3.5" />
                <span className="font-medium">{summary.policyArea}</span>
              </div>
            )}
            {summary.legislativeSubjects && summary.legislativeSubjects.length > 0 && (
              <div className="flex flex-wrap gap-1.5 py-1">
                {summary.legislativeSubjects.map((subject) => (
                  <Badge key={subject} variant="secondary" className="text-xs">
                    {subject}
                  </Badge>
                ))}
              </div>
            )}
            {summary.versionCode === 'none' ? (
              <div className="flex items-center gap-2 text-muted-foreground py-4">
                <AlertCircle className="h-5 w-5" />
                <span>No summary available for this bill.</span>
              </div>
            ) : (
              <div
                className="text-sm text-muted-foreground prose prose-sm max-w-none dark:prose-invert py-2"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(summary.text) }}
              />
            )}
          </>
        ) : (
          <div className="flex items-center gap-2 text-muted-foreground py-8">
            <AlertCircle className="h-5 w-5" />
            <span>No summary available for this bill.</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
